import React from 'react';
import { I18n } from '@aws-amplify/core';
import { Form, Row, message } from 'antd';
import { useParams } from 'react-router-dom';

import { PinCard } from './PinCard';
import { Title, Text } from '@/components/Typography';
import { usePsychosocialAvaliationContext } from '../context';
import { useGetValidateSurveyToken } from '@/hooks/useGetValidateSurveyToken';
import { useInitSurvey } from '@/views/PsychosocialReport/hooks/useInitSurvey';
import { AccessButton, CenteredCol, StyledCard } from '../styles';

interface FormValues {
	employee_code?: string;
	pin?: string[];
}

interface RouteParams {
	token: string;
}

export function EmployeeAccess() {
	const [form] = Form.useForm<FormValues>();
	const { token } = useParams<RouteParams>();
	const { validateSurveyId, nextStep, saveSurveyData } = usePsychosocialAvaliationContext();
	const pinValues = Form.useWatch('pin', form);

	const { isLoading, data, isError } = useGetValidateSurveyToken({
		token_survey: token
	});

	const { isLoading: isLoadingInitSurvey, mutateAsync: initSurvey } = useInitSurvey();

	const isPinComplete = React.useMemo(() => {
		if (!pinValues || !Array.isArray(pinValues)) return false;
		return pinValues.length > 0 && pinValues.every((digit) => digit !== undefined && digit !== '');
	}, [pinValues]);

	async function handleOnSubmit(values: FormValues): Promise<void> {
		const participationId = validateSurveyId(data?.survey_id || '');
		const pinToken = values.pin ? values.pin.join('') : '';

		const surveyResponse = await initSurvey({
			token: pinToken,
			survey_link_id: data?.survey_link_id || '',
			employee_code: values.employee_code || null,
			participation_id: participationId || null
		});

		saveSurveyData({ ...surveyResponse, is_anonymous: data?.anonymous || false });
		nextStep();
	}

	const initialValues = data?.employee_code ? { employee_code: data.employee_code } : {};

	if (isError) {
		message.error(I18n.get('Error validating token'));
		return null;
	}

	return (
		<>
			<Row hidden={!data?.expired}>
				<CenteredCol span={24}>
					<StyledCard>
						<Title level={4}>
							{I18n.get('The psychosocial assessment you attempted to access is no longer available')}
						</Title>
						<Text>{I18n.get(`Evaluation closed on ${data?.expires_at}`)}</Text>
					</StyledCard>
				</CenteredCol>
			</Row>
			<Form
				key={data?.employee_code || ''}
				initialValues={initialValues}
				onFinish={handleOnSubmit}
				hidden={data?.expired}
				scrollToFirstError
				disabled={isError}
				layout="vertical"
				form={form}
			>
				<Row>
					<CenteredCol span={24}>
						<PinCard isAnonymous={data?.anonymous || false} />
					</CenteredCol>
					<CenteredCol span={24}>
						<AccessButton
							disabled={isError || isLoading || isLoadingInitSurvey || !isPinComplete}
							loading={isLoading || isLoadingInitSurvey}
							htmlType="submit"
							type="primary"
						>
							{I18n.get('Access evaluation')}
						</AccessButton>
					</CenteredCol>
				</Row>
			</Form>
		</>
	);
}
